import {
  Icon,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  TabsProps,
  TabListProps,
  TabPanelProps,
  TabPanelsProps,
  Tab as ChakraUITab,
  TabProps as ChakraUITabProps,
  SystemProps,
  ComponentStyleConfig
} from "@chakra-ui/react";
import { ElementType } from "react";
import { mode, getColor, StyleFunctionProps } from "@chakra-ui/theme-tools";

const TabsComponentStyle: ComponentStyleConfig = {
  parts: ["tab", "tablist"],
  baseStyle: (props: StyleFunctionProps) => {
    const { theme, colorScheme } = props;

    return {
      tablist: {
        px: 1,
        py: 2,
        top: 0,
        zIndex: 1,
        overflowX: "auto",
        bg: mode("whiteAlpha.900", "gray.800")(props)
      },
      tab: {
        color: getColor(theme, `${colorScheme}.600`),

        "> svg": {
          color: getColor(theme, `${colorScheme}.700`)
        }
      }
    };
  },
  sizes: {
    md: {
      tab: {
        py: 2,
        px: 4,

        "> svg": {
          width: 4,
          height: 4
        }
      }
    },
    lg: {
      tab: {
        py: 3,
        px: 6,
        fontSize: "md",

        "> svg": {
          width: 5,
          height: 5
        }
      }
    }
  },
  variants: {
    secondary: () => {
      return {
        tab: {
          borderRadius: "lg",
          fontWeight: "normal",
          whiteSpace: "nowrap",
          marginRight: 2,
          borderWidth: "1px",
          borderStyle: "solid",
          color: "gray.800",
          borderColor: "gray.800",
          backgroundColor: "white",

          ":last-child": {
            marginRight: 0
          },
          _hover: { bg: "brand.50" },
          _selected: {
            fontWeight: "semibold",
            bg: "brand.100",
            borderColor: "gray.800"
          }
        }
      };
    },
    "soft-rounded": (props: StyleFunctionProps) => {
      const { theme, colorScheme } = props;

      return {
        tab: {
          borderRadius: "lg",
          fontWeight: "normal",
          whiteSpace: "nowrap",
          marginRight: 2,
          borderWidth: "1px",
          borderStyle: "solid",
          color: getColor(theme, `${colorScheme}.600`),
          borderColor: getColor(theme, `${colorScheme}.200`),

          ":last-child": {
            marginRight: 0
          },

          _selected: {
            fontWeight: "semibold",
            bg: getColor(theme, `${colorScheme}.100`),
            borderColor: getColor(theme, `${colorScheme}.100`)
          }
        }
      };
    }
  },
  defaultProps: {
    colorScheme: "gray"
  }
};

interface TabProps extends ChakraUITabProps {
  leftIcon?: ElementType<any>;
  rightIcon?: ElementType<any>;
  iconSpacing?: SystemProps["marginRight"];
}

function Tab(props: TabProps) {
  const { leftIcon, rightIcon, iconSpacing, children, ...rest } = props;

  if (leftIcon || rightIcon) {
    return (
      <ChakraUITab {...rest}>
        {leftIcon && <Icon as={leftIcon} marginEnd={iconSpacing || 2} />}
        {children}
        {rightIcon && <Icon as={rightIcon} marginStart={iconSpacing || 2} />}
      </ChakraUITab>
    );
  }

  return <ChakraUITab {...rest} />;
}

export { Tab, Tabs, TabList, TabPanel, TabPanels, TabsComponentStyle };

export type {
  TabProps,
  TabsProps,
  TabListProps,
  TabPanelProps,
  TabPanelsProps
};
